import type {CIFinding, CIFindingCheckRun, DedupedCIFinding} from './ci-findings-types'

type CanonicalFinding = {
  finding: CIFinding
  // Category and rule define which findings are semantically eligible for comparison.
  explanationKey: string
  hunkIdentities: string[]
  hunkIdentitySet: Set<string>
  // The sorted hunk set provides a stable tie-breaker when equally large findings compete to represent a group.
  setKey: string
}

function checkRunForFinding(finding: CIFinding): CIFindingCheckRun {
  return {
    id: finding.failedCheckRunId,
    ...(finding.failedCheckRunName ? {name: finding.failedCheckRunName} : {}),
    ...(finding.failedCheckRunUrl ? {url: finding.failedCheckRunUrl} : {}),
  }
}

function mergeCheckRunMetadata(firstSeen: CIFindingCheckRun, additional: CIFindingCheckRun): CIFindingCheckRun {
  const name = firstSeen.name || additional.name
  const url = firstSeen.url || additional.url

  return {
    id: firstSeen.id,
    ...(name && {name}),
    ...(url && {url}),
  }
}

function uniqueCheckRuns(findings: CIFinding[]): CIFindingCheckRun[] {
  const checkRuns = new Map<number, CIFindingCheckRun>()

  for (const finding of findings) {
    const checkRun = checkRunForFinding(finding)
    const existing = checkRuns.get(checkRun.id)
    checkRuns.set(checkRun.id, existing ? mergeCheckRunMetadata(existing, checkRun) : checkRun)
  }

  return [...checkRuns.values()]
}

/** Normalizes platform-specific newlines without changing any other suggestion whitespace. */
function normalizeLineEndings(value: string): string {
  return value.replace(/\r\n?/g, '\n')
}

/** Returns whether a path is a normalized, repository-relative path that is safe to compare as-is. */
export function isCanonicalPath(path: string): boolean {
  if (!path || path.startsWith('/') || path.includes('\\') || path.includes('\0') || /^[A-Za-z]:\//.test(path)) {
    return false
  }
  return path.split('/').every(segment => segment !== '' && segment !== '.' && segment !== '..')
}

/** Returns whether a hunk has a canonical path and a valid inclusive, 1-based original line range. */
function isCanonicalHunk(hunk: NonNullable<CIFinding['fixDiffHunks']>[number]): boolean {
  return (
    isCanonicalPath(hunk.filePath) &&
    Number.isInteger(hunk.startLine) &&
    Number.isInteger(hunk.endLine) &&
    hunk.startLine >= 1 &&
    hunk.endLine >= hunk.startLine
  )
}

/**
 * Serializes the hunk fields that define an identical edit.
 * Returns undefined when the hunk cannot safely participate in containment.
 */
function canonicalHunkIdentity(hunk: NonNullable<CIFinding['fixDiffHunks']>[number]): string | undefined {
  if (!isCanonicalHunk(hunk)) return undefined

  return JSON.stringify([hunk.filePath, hunk.startLine, hunk.endLine, normalizeLineEndings(hunk.suggestion ?? '')])
}

/** Groups findings by normalized category and rule so unrelated explanations are never collapsed together. */
function explanationKey(finding: CIFinding): string {
  return JSON.stringify([(finding.category ?? '').trim().toLowerCase(), (finding.ruleId ?? '').trim().toLowerCase()])
}

/**
 * Converts an eligible code-change finding into a sorted set of canonical raw-hunk identities.
 * Findings with missing or invalid hunks return undefined and remain unique.
 */
function canonicalizeFinding(finding: CIFinding): CanonicalFinding | undefined {
  if (finding.fixType?.trim().toLowerCase() !== 'code_change' || !finding.fixDiffHunks?.length) return undefined
  // Comparing only the valid portion could incorrectly collapse a finding, so one invalid hunk makes it unique.
  if (!finding.fixDiffHunks.every(isCanonicalHunk)) return undefined

  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const hunkIdentities = [...new Set(finding.fixDiffHunks.map(hunk => canonicalHunkIdentity(hunk)!))].sort()

  return {
    finding,
    explanationKey: explanationKey(finding),
    hunkIdentities,
    hunkIdentitySet: new Set(hunkIdentities),
    setKey: JSON.stringify(hunkIdentities),
  }
}

/** Returns whether every hunk identity in subset is present in superset. */
function containsAll(superset: Set<string>, subset: string[]): boolean {
  return subset.every(identity => superset.has(identity))
}

/** Preserves the representative's original hunk payload and order while removing exact duplicate edits. */
function uniqueRepresentativeHunks(representative: CanonicalFinding): NonNullable<CIFinding['fixDiffHunks']> {
  const seen = new Set<string>()
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  return representative.finding.fixDiffHunks!.filter(hunk => {
    const identity = canonicalHunkIdentity(hunk)
    if (!identity || seen.has(identity)) return false
    seen.add(identity)
    return true
  })
}

/** Builds the retained finding and aggregates the IDs and check-run metadata of every collapsed member. */
function toDedupedFinding(representative: CanonicalFinding, members: CIFinding[]): DedupedCIFinding {
  return {
    ...representative.finding,
    fixDiffHunks: uniqueRepresentativeHunks(representative),
    dedupCount: members.length,
    dedupedFindingIds: members.map(finding => finding.id).sort(),
    checkRuns: uniqueCheckRuns(members).sort((left, right) => left.id - right.id),
  }
}

/**
 * Collapses equal and subset raw-hunk sets into deterministic superset representatives.
 * Findings that are ineligible for safe containment are returned as independent results.
 */
export function deduplicateCIFindings(findings: CIFinding[]): DedupedCIFinding[] {
  const canonicalFindings: CanonicalFinding[] = []
  const uniqueFindings: CIFinding[] = []

  // Build canonical raw-hunk sets and separate findings that cannot safely participate in containment.
  for (const finding of findings) {
    const canonicalFinding = canonicalizeFinding(finding)
    if (!canonicalFinding) {
      uniqueFindings.push(finding)
      continue
    }

    canonicalFindings.push(canonicalFinding)
  }

  // Process larger sets first so subsets find a retained superset. The set and finding keys make ties deterministic.
  canonicalFindings.sort(
    (left, right) =>
      right.hunkIdentities.length - left.hunkIdentities.length ||
      left.setKey.localeCompare(right.setKey) ||
      left.finding.id.localeCompare(right.finding.id),
  )

  // Collapse each candidate into the first compatible superset, or retain it as a new representative.
  const representatives: Array<{canonical: CanonicalFinding; members: CIFinding[]}> = []
  for (const candidate of canonicalFindings) {
    const representative = representatives.find(
      group =>
        group.canonical.explanationKey === candidate.explanationKey &&
        containsAll(group.canonical.hunkIdentitySet, candidate.hunkIdentities),
    )

    if (representative) {
      representative.members.push(candidate.finding)
    } else {
      representatives.push({canonical: candidate, members: [candidate.finding]})
    }
  }

  // Aggregate IDs and check-run metadata into representatives, then append findings excluded from containment.
  return [
    ...representatives.map(group => toDedupedFinding(group.canonical, group.members)),
    ...uniqueFindings
      .sort((left, right) => left.id.localeCompare(right.id))
      .map(finding => ({
        ...finding,
        dedupCount: 1,
        dedupedFindingIds: [finding.id],
        checkRuns: [checkRunForFinding(finding)],
      })),
  ]
}
